import React, { useState, useEffect } from 'react';
import './App.css';
import Home from './Home';
import WhatWeDo from './WhatWeDo';
import Projects from './Projects';
import BtsProjects from './BtsProjects';
import BtsVideo from './BtsVideo';
import ContactUs from './ContactUs';
import ServiceDetail from './ServiceDetail';
import AdminPanel from './AdminPanel';
import Navbar from './Navbar';
import Footer from './Footer';
import Founders from './Founders';
import Login from './Login';
import logo from './images/logo.png';
import { initialServices, initialProjects, initialBtsPhotos, initialBtsVideos, initialMovies, initialFounders } from './initialData';
import { fetchData, saveData } from './storageHelper';

function App() {
  const [activeLink, setActiveLink] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const storedAuth = localStorage.getItem('isAdminLoggedIn');
    if (storedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem('isAdminLoggedIn', 'true');
  };

  // Shared State
  const [services, setServices] = useState(initialServices);
  const [projects, setProjects] = useState(initialProjects);
  const [btsPhotos, setBtsPhotos] = useState(initialBtsPhotos);
  const [btsVideos, setBtsVideos] = useState(initialBtsVideos);
  const [trailers, setTrailers] = useState([]);
  const [movies, setMovies] = useState(initialMovies);
  const [founders, setFounders] = useState(initialFounders);
  const [isLoaded, setIsLoaded] = useState(false);

  // Fetch data from S3 on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchData();
        if (data) {
          setServices(data.services || initialServices);
          setProjects(data.projects || initialProjects);
          setBtsPhotos(data.btsPhotos || initialBtsPhotos);
          setBtsVideos(data.btsVideos || initialBtsVideos);
          setTrailers(data.trailers || []);
          setMovies(data.movies || []);
          setFounders(data.founders || initialFounders);
        }
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setIsLoaded(true);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      const scrollPosition = window.scrollY + 150;

      sections.forEach(section => {
        if (
          scrollPosition >= section.offsetTop &&
          scrollPosition < section.offsetTop + section.offsetHeight
        ) {
          setActiveLink(section.id);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getCurrentPage = () => {
    if (window.location.pathname === '/admin') {
      if (!isAuthenticated) return <Login onLogin={handleLogin} />;

      return (
        <AdminPanel
          services={services}
          setServices={setServices}
          projects={projects}
          setProjects={setProjects}
          btsPhotos={btsPhotos}
          setBtsPhotos={setBtsPhotos}
          btsVideos={btsVideos}
          setBtsVideos={setBtsVideos}
          trailers={trailers}
          setTrailers={setTrailers}
          movies={movies}
          setMovies={setMovies}
          founders={founders}
          setFounders={setFounders}
        />
      );
    } else if (window.location.pathname === '/what-we-do') {
      return <WhatWeDo services={services} />;
    } else if (window.location.pathname === '/projects') {
      return <Projects projects={projects} trailers={trailers} movies={movies} />;
    } else if (window.location.pathname === '/founders') {
      return <Founders founders={founders} />;
    } else if (window.location.pathname.startsWith('/bts-projects')) {
      let projectId = null;
      const hash = window.location.hash;

      if (hash.startsWith('#video-')) {
        const idFromHash = parseInt(hash.split('-')[1]);
        if (idFromHash) projectId = idFromHash;
      }

      const filteredBtsPhotos = projectId
        ? btsPhotos.filter(photo => photo.projectId === projectId)
        : btsPhotos;

      const filteredBtsVideos = projectId
        ? btsVideos.filter(video => video.projectId === projectId)
        : btsVideos;

      return (
        <BtsProjects
          photos={filteredBtsPhotos}
          movies={filteredBtsVideos}
          projectId={projectId}
        />
      );
    } else if (window.location.pathname.startsWith('/bts-videos/')) {
      const videoId = window.location.pathname.split('/')[2];
      return <BtsVideo videoId={videoId} movies={btsVideos} trailers={trailers} />;
    } else if (window.location.pathname === '/contact-us') {
      return <ContactUs />;
    } else {
      const service = services.find(s => s.link === window.location.pathname);
      if (service) return <ServiceDetail service={service} />;

      return (
        <Home
          services={services}
          projects={projects}
          btsPhotos={btsPhotos}
          btsVideos={btsVideos}
          trailers={trailers}
          movies={movies}
        />
      );
    }
  };

  const getActivePage = () => {
    if (window.location.pathname === '/admin') return 'admin';
    if (window.location.pathname === '/what-we-do') return 'what-we-do';
    if (window.location.pathname === '/projects') return 'projects';
    if (window.location.pathname === '/bts-projects') return 'bts-project';
    if (window.location.pathname.startsWith('/bts-videos/')) return 'bts-project';
    if (window.location.pathname === '/founders') return 'founders';
    if (window.location.pathname === '/contact-us') return 'contact-us';

    const service = services.find(s => s.link === window.location.pathname);
    if (service) return 'what-we-do';

    if (window.location.pathname === '/') return 'home';
  };

  const activePage = getActivePage();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  // 👉 FIX: don't render pages until data is ready
  if (!isLoaded) {
    return <div class= "load">Rolling cameras…</div>;
  }

  return (
    <div className="App">
      {activePage !== 'admin' && <Navbar activePage={activePage} />}
      {getCurrentPage()}
      {activePage !== 'admin' && <Footer activePage={activePage} />}
    </div>
  );
}

export default App;
